import { Apis, Constants, Enums, Helpers, Store } from "utils";
import { IBroadcast } from "interfaces";

export interface IBroadcastService {
  session(): IBroadcast[];
  slice(broadcastType: string, state?: boolean, offset?: number, count?: number): Promise<any>;
  announcement(message: string): Promise<any>;
  reminder(message: string, users: number[]): Promise<any>;
  delete(id: number): Promise<any>;
  update(id: number, state: boolean): Promise<any>;
  read(id: number): Promise<any>;
}

export class BroadcastService implements IBroadcastService {
  private qubehub: any;
  private controllerName: string = "broadcast";

  constructor(accessToken: string) {
    this.qubehub = Apis.qubehub(accessToken);
  }

  /**
   * fetch all the announcements for an user
   * @returns
   */

  session(): IBroadcast[] {
    try {
      return Store.sessionStorage.get().announcements ?? [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * fetch all the announcements/reminders
   * @returns
   */

  async slice(broadcastType: string, state: boolean, offset: number = 0, count: number = Constants.PAGE_LENGTH) {
    try {
      const props = { broadcastType, offset, count };
      const queryString = Helpers.toQueryString(this.controllerName, state ? { ...props, received: state } : props);
      const [status, response] = this.#handleResponse(await this.qubehub.get(queryString));
      return response ? response.broadcasts : [];
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * send a message to all the patients of a clinic
   * @param {string} message the content of the message
   * @returns
   */

  async announcement(message: string) {
    try {
      return this.#handleResponse(await this.qubehub.post(this.controllerName, { message }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * send a reminder to the patients
   * @param {string} message the content of the reminder
   * @param {number[]} users the user ids
   * @returns
   */

  async reminder(message: string, users: number[]) {
    try {
      return this.#handleResponse(await this.qubehub.post(this.controllerName, { message, receiverIds: users.join(",") }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * delete the announcement/reminder
   * @param {number} id broadcast id (announcement/reminder)
   * @returns
   */

  async delete(id: number) {
    try {
      this.#handleResponse(await this.qubehub.delete(Helpers.interpolateURL("broadcast/:broadcastId", { broadcastId: id })));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * update the message status (read)
   * @param {number} id message id
   * @param {boolean} state true if read
   * @returns
   */

  async update(id: number, state: boolean) {
    try {
      this.#handleResponse(await this.qubehub.patch(Helpers.interpolateURL(this.controllerName + "/:broadcastId", { broadcastId: id }), { received: state }));
    } catch (err: any) {
      console.log(err);
    }
    return null;
  }

  /**
   * delete message from session
   * @param {number} id message id
   * @returns
   */

  async read(id: number) {
    try {
      await this.update(id, true);
      const announcements = this.session();
      const newAnnouncements = announcements.filter((item: IBroadcast) => item.broadcastId !== id);
      Store.sessionStorage.set({ announcements: newAnnouncements });
      return newAnnouncements;
    } catch (err: any) {
      console.log(err);
    }
    return [];
  }

  /**
   * method to handle the response from the API
   * @param {any} response the data retrieved from the API
   * @returns
   */

  #handleResponse(response: any) {
    if (response && response.data) {
      const { status, payload } = response.data;
      if (Enums.EnumResponse.Unauthorized === status) Store.sessionStorage.clear();
      return payload ? [status, payload] : [status, null];
    }
    return [Enums.EnumResponse.None, null];
  }
}

export default BroadcastService;
